// Tiền của một đơn theo góc nhìn người xem (GĐ7, §2.8, §4.6) — đọc `order.money` do API trả.
//  · Người bán thấy "Thực nhận" (có thể âm = công nợ).
//  · Cấp trên (Đại lý / CTV) thấy chênh lệch / hoa hồng của chính mình trên đơn.
// Không bao giờ hiện giá nhập của người khác hay giá gốc BEST — API cũng không trả các số đó.
// `money: null` = đơn không có sổ tiền: đơn không tạo vận đơn (không có COD qua BEST), hoặc
// đơn cũ tạo trước khi có sổ tiền — nói rõ là trường hợp nào, không ước tính.

import type { ReactNode } from 'react'
import { money as fmtMoney } from '@/lib/format'
import { cn } from '@/lib/utils'

export type MoneyStage = 'in_transit' | 'awaiting_carrier' | 'ready' | 'in_voucher' | 'paid' | 'held' | 'void' | 'none'

export interface OrderMoneyView {
  financeId: string
  status: 'draft' | 'open' | 'delivered' | 'returned' | 'lost' | 'cancelled'
  returnStage: 'returning' | 'returned' | 'return_failed' | null
  codExpected: number
  codActual: number | null
  customerShippingFee: number
  /** Cước theo bảng giá của người bán lúc tạo đơn — chỉ có khi người xem là người bán. */
  sellerPrice?: number | null
  /** Tổng dòng tiền của người xem trên đơn (có dấu; âm = công nợ). */
  amount: number
  stage: MoneyStage
  holdReason: 'debt_recovery' | 'payee_locked' | null
  voucherCode: string | null
  /** seller_net, margin, ctv_commission, commission_deduction, adjustment… */
  byKind: Record<string, number>
  /** Hạn báo thiếu / hỏng hàng hoàn (thời điểm 708 + returnClaimDays); null = chưa trả hàng về. */
  returnClaimDeadline?: string | null
}

export const HOLD_REASON_LABEL: Record<string, string> = {
  debt_recovery: 'chờ thu nợ của shop',
  payee_locked: 'tài khoản bị khóa',
}

const STAGE_META: Record<MoneyStage, { text: string; cls: string }> = {
  in_transit: { text: 'Chờ giao xong', cls: 'bg-ship/12 text-ship' },
  awaiting_carrier: { text: 'Chờ hãng thanh toán', cls: 'bg-info/12 text-info' },
  ready: { text: 'Sẵn sàng chi', cls: 'bg-ok/12 text-ok' },
  in_voucher: { text: 'Đã lên phiếu', cls: 'bg-accent/12 text-accent' },
  paid: { text: 'Đã chi', cls: 'bg-ok/12 text-ok' },
  held: { text: 'Tạm giữ', cls: 'bg-warn/12 text-warn' },
  void: { text: 'Đã hủy', cls: 'bg-paperSoft text-inkSoft' },
  none: { text: '—', cls: 'bg-paperSoft text-inkSoft' },
}

/** Loại dòng tiền → nhãn. Chỉ các loại một đơn vị có thể thấy trên đơn của mình. */
export const MONEY_KIND_LABEL: Record<string, string> = {
  seller_net: 'COD trừ cước',
  margin: 'Chênh lệch cước',
  ctv_commission: 'Chiết khấu CTV',
  commission_deduction: 'Chiết khấu trả CTV',
  carrier_surcharge: 'Phí ngoài khung',
  carrier_compensation: 'Hãng bồi thường',
  adjustment: 'Điều chỉnh',
  ship_prepaid: 'Tiền ship đã chuyển khoản trước',
  debt_payment: 'Trả nợ',
  writeoff: 'Xóa nợ',
}

const KIND_ORDER = ['seller_net', 'carrier_surcharge', 'carrier_compensation', 'ship_prepaid', 'margin', 'ctv_commission', 'commission_deduction', 'adjustment', 'debt_payment', 'writeoff']

const RETURN_STAGE_LABEL: Record<string, string> = {
  returning: 'Đang trả hàng về',
  returned: 'Đã trả hàng về',
  return_failed: 'Trả hàng không thành công',
}

/** Người xem là người bán của đơn này? (có dòng thực nhận) */
export function isSellerMoney(m: OrderMoneyView): boolean {
  return m.byKind.seller_net !== undefined
}

/** `debtorName`: đơn vị đang nợ (khi tạm giữ chờ thu nợ) — "chờ thu nợ của Shop A". */
export function moneyStageText(m: Pick<OrderMoneyView, 'stage' | 'voucherCode' | 'holdReason'>, debtorName?: string | null): string {
  if (m.stage === 'in_voucher') return m.voucherCode ? `Đã lên phiếu ${m.voucherCode}` : 'Đã lên phiếu'
  if (m.stage === 'held') {
    if (m.holdReason === 'debt_recovery' && debtorName) return `Tạm giữ — chờ thu nợ của ${debtorName}`
    const reason = m.holdReason ? HOLD_REASON_LABEL[m.holdReason] : undefined
    return reason ? `Tạm giữ — ${reason}` : 'Tạm giữ'
  }
  return STAGE_META[m.stage]?.text ?? '—'
}

/** Nhãn trạng thái tiền: Chờ giao xong · Chờ BEST thanh toán · Sẵn sàng chi · Trong phiếu … · Đã chi · Tạm giữ. */
export function MoneyStageBadge({
  money,
  debtorName,
  className,
}: {
  money: Pick<OrderMoneyView, 'stage' | 'voucherCode' | 'holdReason'>
  debtorName?: string | null
  className?: string
}) {
  if (money.stage === 'none') return <span className={cn('text-xs text-muted', className)}>—</span>
  const meta = STAGE_META[money.stage] ?? STAGE_META.none
  return (
    <span className={cn('inline-flex max-w-full items-start gap-1.5 rounded-full px-2 py-0.5 text-left text-xs font-medium', meta.cls, className)}>
      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-current" aria-hidden />
      <span className="min-w-0 break-words">{moneyStageText(money, debtorName)}</span>
    </span>
  )
}

/** Số tiền có dấu: âm đỏ (kèm "công nợ" nếu `debtTag`); cấp trên thấy dấu + cho khoản dương. */
export function SignedAmount({ value, plus = false, debtTag = true, className }: { value: number; plus?: boolean; debtTag?: boolean; className?: string }) {
  const negative = value < 0
  return (
    <span className={cn('whitespace-nowrap font-mono tabular-nums', negative ? 'text-danger' : value > 0 ? 'text-ink' : 'text-muted', className)}>
      {plus && value > 0 ? '+' : ''}
      {fmtMoney(value)}
      {negative && debtTag && <span className="ml-1.5 rounded bg-danger/10 px-1 py-px font-sans text-[11px] font-medium">công nợ</span>}
    </span>
  )
}

export const NO_LEDGER_HINT = 'Đơn tạo trước khi có sổ tiền.'
export const NO_SHIPMENT_LEDGER_HINT = 'Đơn không tạo vận đơn — không có sổ tiền.'

/** Lý do đơn không có sổ tiền: `hasShipment === false` = đơn không tạo vận đơn; còn lại = đơn cũ. */
export function noLedgerHint(hasShipment?: boolean): string {
  return hasShipment === false ? NO_SHIPMENT_LEDGER_HINT : NO_LEDGER_HINT
}

/**
 * Ô "tiền của bạn" trong bảng đơn.
 * `seller`: ngữ cảnh mặc định — người xem là người bán (thực nhận) hay cấp trên (chênh lệch /
 * hoa hồng); dòng tiền của đơn (có `seller_net` hay không) được ưu tiên hơn.
 * `showRole`: bảng trộn cả hai loại đơn — ghi rõ con số là gì.
 */
export function OrderMoneyCell({
  money,
  seller,
  showRole = false,
  hasShipment,
}: {
  money: OrderMoneyView | null | undefined
  seller: boolean
  showRole?: boolean
  /** Đơn có vận đơn không — để nói đúng lý do khi đơn không có sổ tiền. */
  hasShipment?: boolean
}) {
  if (!money) {
    return (
      <span className="text-xs text-muted">
        {hasShipment === false ? 'Không vận đơn' : 'Đơn cũ — chưa tính tiền'}
      </span>
    )
  }
  // Dòng tiền tự nói người xem là ai; chỉ khi đơn chưa có dòng nào (đã hủy…) mới dựa vào ngữ cảnh.
  const isSeller = Object.keys(money.byKind).length > 0 ? isSellerMoney(money) : seller
  return (
    <span className="flex min-w-0 flex-col items-end gap-1 mobile:items-start">
      {money.stage === 'void' ? (
        <span className="font-mono text-muted">—</span>
      ) : (
        <SignedAmount value={money.amount} plus={!isSeller} className="font-semibold" />
      )}
      {showRole && money.stage !== 'void' && <span className="text-xs text-muted">{isSeller ? 'Thực nhận' : 'Chênh lệch / chiết khấu'}</span>}
      {/* Bảng đơn đã có cột Trạng thái: "chờ giao xong" / "đã hủy" chỉ lặp lại trạng thái đơn
          (đọc như đơn có hai trạng thái) — chỉ hiện nhãn tiền khi nó nói thêm điều gì đó. */}
      {money.stage === 'in_transit' ? (
        <span className="text-xs text-muted">tạm tính</span>
      ) : money.stage !== 'void' ? (
        <MoneyStageBadge money={money} />
      ) : null}
    </span>
  )
}

/**
 * Nội dung thẻ "Tiền của đơn" ở trang chi tiết đơn — mỗi dòng nhãn trái / số phải (vừa cột hẹp bên phải).
 * `seller`: ngữ cảnh — người xem là người bán của đơn (mặc định) hay cấp trên. Chỉ dùng khi
 * đơn chưa có dòng tiền nào (vd. đơn đã hủy); có dòng tiền thì dòng tiền tự nói người xem là ai.
 * `cod`: COD trên đơn — hiện khi đơn không có sổ tiền. `cost`: giá vốn (chỉ chủ đơn thấy).
 */
export function OrderMoneyDetails({
  money,
  seller: sellerContext = true,
  hasShipment,
  cod,
  cost,
}: {
  money: OrderMoneyView | null | undefined
  seller?: boolean
  hasShipment?: boolean
  cod?: number
  cost?: number
}) {
  const costRow = cost !== undefined && (
    <Row label="Giá vốn">
      <span className="font-mono tabular-nums text-ink">{fmtMoney(cost)}</span>
    </Row>
  )

  if (!money) {
    return (
      <div className="space-y-2 text-sm">
        {(cod !== undefined || costRow) && (
          <dl className="space-y-1">
            {cod !== undefined && (
              <Row label="COD">
                <span className="font-mono tabular-nums text-ink">{fmtMoney(cod)}</span>
              </Row>
            )}
            {costRow}
          </dl>
        )}
        <p className="text-xs text-muted">{noLedgerHint(hasShipment)}</p>
      </div>
    )
  }

  const seller = Object.keys(money.byKind).length > 0 ? isSellerMoney(money) : sellerContext
  const kinds = Object.entries(money.byKind)
    .filter(([, v]) => v !== 0)
    .sort(([a], [b]) => rank(a) - rank(b))

  return (
    <div className="space-y-2.5 text-sm">
      <dl className="space-y-1">
        <Row label="COD dự kiến">
          <span className="font-mono tabular-nums text-ink">{fmtMoney(money.codExpected)}</span>
        </Row>
        <Row label="COD đã thu">
          <span className={cn('font-mono tabular-nums', money.codActual == null ? 'text-muted' : 'text-ink')}>
            {money.codActual == null ? 'Chưa thu' : fmtMoney(money.codActual)}
          </span>
        </Row>
        <Row label="Phí ship thu khách">
          <span className="font-mono tabular-nums text-ink">{fmtMoney(money.customerShippingFee)}</span>
        </Row>
        {costRow}
        <Row label="Trạng thái tiền">
          <MoneyStageBadge money={money} />
        </Row>
        {money.voucherCode && (
          <Row label="Phiếu đối soát">
            <span className="font-mono text-ink">{money.voucherCode}</span>
          </Row>
        )}
        {money.returnStage && (
          <Row label="Hoàn hàng">
            <span className="text-ink">{RETURN_STAGE_LABEL[money.returnStage] ?? money.returnStage}</span>
          </Row>
        )}
      </dl>

      <div className="border-t border-rule pt-2.5">
        <p className="mb-1.5 text-xs font-medium text-muted">{seller ? 'Số tiền của bạn' : 'Chênh lệch / chiết khấu của bạn'}</p>
        {money.stage === 'void' ? (
          <p className="text-sm text-muted">Đơn đã hủy — không phát sinh tiền.</p>
        ) : (
          <dl className="space-y-1">
            {/* Một khoản duy nhất thì dòng tổng đã nói đủ */}
            {kinds.length > 1 &&
              kinds.map(([kind, value]) => (
                <Row key={kind} label={MONEY_KIND_LABEL[kind] ?? 'Khoản khác'}>
                  <SignedAmount value={value} plus={!seller} debtTag={false} />
                </Row>
              ))}
            <Row
              label={<span className="font-medium text-ink">{seller ? 'Thực nhận' : kinds.length === 1 ? (MONEY_KIND_LABEL[kinds[0]![0]] ?? 'Của bạn') : 'Tổng của bạn'}</span>}
              className={cn(kinds.length > 1 && 'border-t border-rule pt-1')}
            >
              <SignedAmount value={money.amount} plus={!seller} className="font-semibold" />
            </Row>
          </dl>
        )}
      </div>
    </div>
  )
}

function rank(kind: string): number {
  const i = KIND_ORDER.indexOf(kind)
  return i === -1 ? KIND_ORDER.length : i
}

function Row({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-3', className)}>
      <dt className="min-w-0 text-inkSoft">{label}</dt>
      <dd className="shrink-0 text-right">{children}</dd>
    </div>
  )
}
